import Service, { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import { cloneCardStackAttributesWithNewIds } from "@shared/lib/card-stack/clone"
import type { CardStackAttributes } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import DateAnnotation from "@shared/models/date-annotation"
import type {
  Planbook,
  PlanbookDateAnnotationDateRange,
  PlanbookDateAnnotationSingle,
} from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import ObjectId from "@shared/util/object-id-gen"
import { prepareBumpAllLessonsInRangeToDate } from "cc-frontend/lib/actions/course/calendar/BUMP_ALL_LESSONS_IN_RANGE_TO_DATE"
import {
  insertNewTemplate,
  prepareSaveAsLessonTemplate,
} from "cc-frontend/lib/actions/course/calendar/COURSE_DATE_SAVE_AS_TEMPLATE"
import preparePlanbookAddSpecialEvent from "cc-frontend/lib/actions/planbook/date/PLANBOOK_DATE_ANNOTATION_ADD_SPECIAL_EVENT"
import preparePlanbookDateAnnotationUpdate from "cc-frontend/lib/actions/planbook/date/PLANBOOK_DATE_ANNOTATION_UPDATE"
import { prepareEraseAllLessonsForDays } from "cc-frontend/lib/actions/planbook/lessons/PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS"
import prepareUpdatePlanbookIntegration from "cc-frontend/lib/actions/planbook/PLANBOOK_UPDATE_INTEGRATION"
import moment from "cc-frontend/utils/extended-moment"
import { cloneDeep, isNil } from "lodash-es"
import type { PreparedAction } from "../lib/action-doc-builder"
import type Dispatcher from "../services/dispatcher"
import type EmberStore from "../services/ember-store"

export default class PlanbookEditor extends Service {
  // Services
  @service dispatcher!: Dispatcher
  @service("ember-store") store!: EmberStore

  /*-------------------------------------------------------------------------------------------------------
  Planbook Date
  --------------------------------------------------------------------------------------------------------*/

  addAnnotation({
    title,
    description,
    startDate,
    endDate,
    isOff,
    models,
    bumpDeleteOrNothing,
  }: {
    title: string
    description: string
    startDate: string
    endDate: string | null
    isOff: boolean
    models: {
      planbook: Planbook
      rotationCalendar: RotationCalendar
      courses: Array<Course>
    }
    bumpDeleteOrNothing: "BUMP" | "DELETE" | "NOTHING"
  }) {
    endDate = endDate ?? startDate

    let dateAnnotation = this.checkAnnotationType(title, description, startDate, endDate, isOff)
    const bumpDeleteOrNothingAction = this.bumpDeleteOrNothingAction(
      startDate,
      endDate,
      models,
      bumpDeleteOrNothing
    )
    this.dispatcher.dispatchPreparedAction(
      preparePlanbookAddSpecialEvent({
        dateAnnotation: dateAnnotation,
        planbook: models.planbook,
        bumpDeleteOrNothing,
        bumpDeleteOrNothingPatches: bumpDeleteOrNothingAction?.patches ?? [],
        bumpDeleteOrNothingUndoPatches: bumpDeleteOrNothingAction?.undoPatches ?? [],
      })
    )
  }

  updateAnnotation({
    oldAnnotation,
    title,
    description,
    startDate,
    endDate,
    isOff,
    models,
    bumpDeleteOrNothing,
  }: {
    oldAnnotation: PlanbookDateAnnotationSingle | PlanbookDateAnnotationDateRange
    title: string
    description: string
    startDate: string
    endDate: string | null
    isOff: boolean
    models: {
      planbook: Planbook
      rotationCalendar: RotationCalendar
      courses: Array<Course>
    }
    bumpDeleteOrNothing: "BUMP" | "DELETE" | "NOTHING"
  }) {
    endDate = endDate ?? startDate

    let newAnnotation = cloneDeep(oldAnnotation)
    newAnnotation.attributes.title = title
    newAnnotation.attributes.description = description
    if (newAnnotation.type === "date-annotation-planbook-custom-range") {
      newAnnotation.attributes.startDate = startDate
      newAnnotation.attributes.endDate = endDate
    } else {
      newAnnotation.attributes.dateString = startDate
    }
    newAnnotation.attributes.isOff = isOff

    const bumpDeleteOrNothingAction = this.bumpDeleteOrNothingAction(
      startDate,
      endDate,
      models,
      bumpDeleteOrNothing
    )
    this.dispatcher.dispatchPreparedAction(
      preparePlanbookDateAnnotationUpdate({
        annotation: newAnnotation,
        planbook: models.planbook,
        bumpDeleteOrNothing,
        bumpDeleteOrNothingPatches: bumpDeleteOrNothingAction?.patches ?? [],
        bumpDeleteOrNothingUndoPatches: bumpDeleteOrNothingAction?.undoPatches ?? [],
      })
    )
  }

  /**
   * Returns an action that bumps, deletes, or does nothing as instructed,
   * for lessons in the given date range.
   *
   * We extract the actions from this patch and combine them with the add/edit annotation action.
   */
  private bumpDeleteOrNothingAction(
    startDate: string,
    endDate: string,
    models: {
      planbook: Planbook
      rotationCalendar: RotationCalendar
      courses: Array<Course>
    },
    bumpDeleteOrNothing: "BUMP" | "DELETE" | "NOTHING"
  ): PreparedAction | null {
    const validSchoolDays = models.rotationCalendar.attributes.schoolDays
    const range = moment.range(moment(startDate), moment(endDate))
    const dateStrings = Array.from(range.by("days"))
      .filter((moment) => validSchoolDays.includes(moment.weekday()))
      .map((moment) => moment.format("YYYY-MM-DD"))
    if (dateStrings.length === 0) return null

    switch (bumpDeleteOrNothing) {
      case "BUMP":
        return prepareBumpAllLessonsInRangeToDate({
          courses: models.courses,
          startDate,
          endDate,
          dateStrings,
          planbook: models.planbook,
          rotationCalendar: models.rotationCalendar,
        })
      case "DELETE": {
        return prepareEraseAllLessonsForDays({
          courses: models.courses,
          dateStrings,
          planbookId: models.planbook.id,
        })
      }
    }
    return null
  }

  checkAnnotationType(title, description, startDate, endDate, isOff) {
    let dateAnnotation
    let isRange = !isNil(endDate) && startDate !== endDate
    if (isRange) {
      dateAnnotation = new DateAnnotation({
        id: ObjectId.create(),
        type: "date-annotation-planbook-custom-range",
        attributes: {
          startDate: dateFns.format(startDate, "YYYY-MM-DD"),
          endDate: dateFns.format(endDate, "YYYY-MM-DD"),
          title: title,
          description: description,
          showAnnotation: true,
          isOff: isOff,
        },
      })
    } else {
      dateAnnotation = new DateAnnotation({
        id: ObjectId.create(),
        type: "date-annotation-planbook-custom-single",
        attributes: {
          dateString: dateFns.format(startDate, "YYYY-MM-DD"),
          title: title,
          description: description,
          showAnnotation: true,
          isOff: isOff,
        },
      })
    }
    return dateAnnotation
  }

  /*-------------------------------------------------------------------------------------------------------
  Template Related
  --------------------------------------------------------------------------------------------------------*/

  saveAsLessonTemplate(
    course: Course,
    planbook: Planbook,
    cardStackAttributes: CardStackAttributes,
    templateTitle: string
  ) {
    let id = ObjectId.create()
    // Deep-copies the source lesson and assigns fresh card/character ids. The template's
    // usedAs is set to "lesson-template" by COURSE_DATE_SAVE_AS_TEMPLATE.
    let lessonAttributes = cloneCardStackAttributesWithNewIds(
      cardStackAttributes,
      cardStackAttributes.parentCardStack,
      !!planbook.attributes.flags.unifiedLessons,
      {
        overrideTitle: templateTitle,
        onMigrationError: (error) => Sentry.captureException(error),
      }
    )

    insertNewTemplate(this.store, id, planbook.id, lessonAttributes)
    this.dispatcher.dispatchPreparedAction(
      prepareSaveAsLessonTemplate({
        newId: id,
        planbookId: planbook.id,
        cardStackAttributes: lessonAttributes,
      })
    )
  }

  /*-------------------------------------------------------------------------------------------------------
  Planbook Integration Stuff
  --------------------------------------------------------------------------------------------------------*/

  updatePlanbookIntegration(planbookId: string, integrationName: string, prop: string, value: any) {
    this.dispatcher.dispatchPreparedAction(
      prepareUpdatePlanbookIntegration({
        planbookId: planbookId,
        integrationName: integrationName,
        prop: prop,
        value: value,
      })
    )
  }
}
